import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../UI/card';
import FeatureCard from './FeatureCard';
import PassageText from './PassageText';
import { getPassageText } from './passageLoader';
import { getConstructPassage, getResilienceQuadrantPassage } from './passageParser';

const ConstructSection = ({ constructType, gameData, constructPercentiles }) => {
  console.log("constructType ::", constructType);
  
  // Get the color scheme based on construct type
  const getConstructColor = (construct) => {
    const colorMap = {
      'Resilience': { primary: 'emerald', secondary: 'green' },
      'Persistence': { primary: 'blue', secondary: 'sky' },
      'ProblemSolving': { primary: 'purple', secondary: 'violet' },
      'GoalSetting': { primary: 'orange', secondary: 'amber' },
      'ChallengeSeeking': { primary: 'red', secondary: 'rose' },
    };
    return colorMap[construct] || { primary: 'gray', secondary: 'slate' };
  };

  const colors = getConstructColor(constructType);

  // Get relevant percentiles for this construct
  const relevantPercentiles = useMemo(() => {
    const filtered = {};
    Object.entries(constructPercentiles).forEach(([key, value]) => {
      if (key.toLowerCase().includes(constructType.toLowerCase())) {
        filtered[key] = value;
      }
    });
    return filtered;
  }, [constructPercentiles, constructType]);

  // Get the appropriate passage for this construct
  const constructPassage = useMemo(() => {
    const passageText = getPassageText(constructType);
    if (!passageText) return null;

    // Special handling for Resilience (quadrant-based)
    if (constructType === 'Resilience') {
      const positivePercentile = constructPercentiles['Slide_Resilience_PositivePercentile'] || 50;
      const negativePercentile = constructPercentiles['Slide_Resilience_NegativePercentile'] || 50;
      return getResilienceQuadrantPassage(positivePercentile, negativePercentile, passageText);
    }

    // For other constructs, use the main percentile
    const mainPercentileKey = Object.keys(relevantPercentiles)[0];
    const mainPercentile = mainPercentileKey ? relevantPercentiles[mainPercentileKey] : 50;

    return getConstructPassage(constructType, mainPercentile, { [constructType]: passageText });
  }, [constructType, constructPercentiles, relevantPercentiles]);

  // Format construct name for display
  const displayName = constructType.replace(/([A-Z])/g, ' $1').trim();

  // Get construct icon
  const getConstructIcon = (construct) => {
    const iconMap = {
      'Resilience': (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      ),
      'Persistence': (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      ),
      'ProblemSolving': (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      ),
      'GoalSetting': (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      ),
      'ChallengeSeeking': (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      ),
    };
    return iconMap[construct] || (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    );
  };

  // Create dynamic styles to avoid Tailwind purging issues
  const cardStyle = {
    borderColor: colors.primary === 'emerald' ? '#a7f3d0' :
                 colors.primary === 'blue' ? '#bfdbfe' :
                 colors.primary === 'purple' ? '#c4b5fd' :
                 colors.primary === 'orange' ? '#fed7aa' :
                 colors.primary === 'red' ? '#fecaca' : '#d1d5db',
    backgroundColor: colors.primary === 'emerald' ? '#ecfdf5' :
                     colors.primary === 'blue' ? '#eff6ff' :
                     colors.primary === 'purple' ? '#f5f3ff' :
                     colors.primary === 'orange' ? '#fff7ed' :
                     colors.primary === 'red' ? '#fef2f2' : '#f9fafb'
  };

  const headerStyle = {
    background: colors.primary === 'emerald' ? 'linear-gradient(to right, rgba(16, 185, 129, 0.8), rgba(5, 150, 105, 0.8))' :
                colors.primary === 'blue' ? 'linear-gradient(to right, rgba(59, 130, 246, 0.8), rgba(14, 165, 233, 0.8))' :
                colors.primary === 'purple' ? 'linear-gradient(to right, rgba(139, 92, 246, 0.8), rgba(124, 58, 237, 0.8))' :
                colors.primary === 'orange' ? 'linear-gradient(to right, rgba(249, 115, 22, 0.8), rgba(245, 158, 11, 0.8))' :
                colors.primary === 'red' ? 'linear-gradient(to right, rgba(239, 68, 68, 0.8), rgba(244, 63, 94, 0.8))' :
                'linear-gradient(to right, rgba(107, 114, 128, 0.8), rgba(75, 85, 99, 0.8))'
  };

  const gameHeaderColor = colors.primary === 'emerald' ? 'text-emerald-700' :
                          colors.primary === 'blue' ? 'text-blue-700' :
                          colors.primary === 'purple' ? 'text-purple-700' :
                          colors.primary === 'orange' ? 'text-orange-700' :
                          colors.primary === 'red' ? 'text-red-700' : 'text-gray-700';

  const borderColor = colors.primary === 'emerald' ? '#a7f3d0' :
                      colors.primary === 'blue' ? '#bfdbfe' :
                      colors.primary === 'purple' ? '#c4b5fd' :
                      colors.primary === 'orange' ? '#fed7aa' :
                      colors.primary === 'red' ? '#fecaca' : '#d1d5db';

  return (
    <Card className="border" style={cardStyle}>
      <CardHeader className="text-white" style={headerStyle}>
        <CardTitle className="text-xl font-semibold flex items-center">
          <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {getConstructIcon(constructType)}
          </svg>
          {displayName}
        </CardTitle>

        {/* Display overall percentiles if available */}
        {Object.keys(relevantPercentiles).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-3">
            {Object.entries(relevantPercentiles).map(([key, value]) => (
              <div key={key} className="bg-white bg-opacity-20 rounded-lg px-3 py-1">
                <span className="text-sm font-medium">
                  {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}: {Math.round(+value)}%
                </span>
              </div>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="p-6">
        {/* Display the construct passage if available */}
        {constructPassage && (
          <div className="mb-6">
            <PassageText passage={constructPassage} colors={colors} />
          </div>
        )}

        {/* Display the feature data */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(gameData).map(([gameType, features]) => (
            <div key={gameType} className="space-y-3">
              <h4 className={`text-lg font-semibold ${gameHeaderColor} border-b pb-2`} style={{ borderColor }}>
                {gameType} Game
              </h4>
              <div className="space-y-2">
                {Object.entries(features).map(([featureName, featureData]) => (
                  <FeatureCard
                    key={featureName}
                    featureName={featureName}
                    featureData={featureData}
                    colors={colors}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ConstructSection;
